import { Divider, Stack, Typography } from '@mui/material';
import { ICellRendererParams } from 'ag-grid-community';
import React from 'react';
import { useNavigate } from 'react-router';

import { MoreHorizIcon } from '@/shared/assets/icons/more-horiz-icon';
import { IconButton } from '@/shared/components/ui/button/icon-button/icon-button';
import { useDataTable } from '@/shared/components/ui/data-table/context/data-table-context';
import { NameContainer } from '@/shared/components/ui/data-table/data-table.styled';
import { PopoverController } from '@/shared/components/ui/popover/components/popover-controller/popover-controller';
import { PopoverOptions } from '@/shared/components/ui/popover/components/popover-options/popover-options.styled';
import { SearchableResourceField } from '@/shared/components/ui/searchable-resource-field/searchable-resource-field';
import { Tags } from '@/shared/components/ui/tags/tags';
import { GroupedItemContextMenu } from '@/shared/features/groups/components/grouped-item-context-menu/grouped-item-context-menu';
import { usePermissionCheck } from '@/shared/features/permissions/hooks/use-permission-check/use-permission-check';
import { DeleteUser } from '@/shared/features/rbac/components/delete-user/delete-user';
import { UpdateUser } from '@/shared/features/rbac/components/update-user/update-user';
import { UserIdentificationChips } from '@/shared/features/rbac/components/user-identification-chips/user-identification-chips';
import { UserSourceChip } from '@/shared/features/rbac/components/user-source-chip/user-source-chip';
import { useRbacConfig } from '@/shared/features/rbac/context/rbac-config-context';
import { useLoggedInUser } from '@/shared/hooks/use-logged-in-user/use-logged-in-user';
import { UserFragment } from '@/shared/types/types.generated';

export const FirstNameRenderer: React.FC<ICellRendererParams<UserFragment>> = ({
  data,
}) => {
  const { getItemLink } = useDataTable();
  const navigate = useNavigate();
  const link = getItemLink?.(data) ?? '';

  return (
    <NameContainer
      $clickable={Boolean(link)}
      onClick={() => navigate(link)}
      height="100%"
    >
      <Typography variant="p7">
        <SearchableResourceField
          value={data?.firstName ?? ''}
          queryParam="name"
        />
      </Typography>
    </NameContainer>
  );
};

export const LastNameRenderer: React.FC<ICellRendererParams<UserFragment>> = ({
  data,
}) => {
  const { getItemLink } = useDataTable();
  const navigate = useNavigate();
  const link = getItemLink?.(data) ?? '';

  return (
    <NameContainer
      $clickable={Boolean(link)}
      onClick={() => navigate(link)}
      height="100%"
    >
      <Typography variant="p7">
        <SearchableResourceField
          value={data?.lastName ?? ''}
          queryParam="name"
        />
      </Typography>
    </NameContainer>
  );
};

export const UserRolesRenderer: React.FC<ICellRendererParams> = ({ data }) => {
  return (
    <Stack height="100%" justifyContent="center">
      <Tags tags={data.roleTags} visibleTags={1} />
    </Stack>
  );
};

export const UserSourceRenderer: React.FC<ICellRendererParams> = ({ data }) => {
  return <UserSourceChip userSource={data.source} />;
};

export const UserIdentificationRenderer: React.FC<ICellRendererParams> = ({
  data,
}) => {
  return <UserIdentificationChips userId={data.userId} />;
};

export const UserActionsRenderer: React.FC<ICellRendererParams> = ({
  data,
}) => {
  const { rbacPermissionChecks, permissionActions } = useRbacConfig();
  const { isLoggedInUser, isOwner } = useLoggedInUser();
  const { canAny } = usePermissionCheck();
  // Self always sees the menu (to self-edit); others only when updatable/deletable.
  const showActions =
    isLoggedInUser(data.userId) ||
    canAny(
      [permissionActions.updateUser, permissionActions.deleteUser],
      data.userId,
    );

  const renderPopoverOptions = (handleClose: () => void) => {
    return (
      <>
        <UpdateUser user={data.raw} onClose={handleClose} />
        {!isLoggedInUser(data.userId) && !isOwner(data.userId) && (
          <>
            <Divider />
            <DeleteUser
              component="MenuItem"
              userIds={[data.userId]}
              userSource={data.source}
            />
          </>
        )}
      </>
    );
  };

  return (
    <Stack
      direction="row"
      gap="1rem"
      alignItems="center"
      justifyContent="flex-end"
      height="100%"
    >
      <UserIdentificationChips userId={data.userId} />
      {showActions && (
        <PopoverController
          trigger={
            <IconButton variant="secondary">
              <MoreHorizIcon />
            </IconButton>
          }
          permission={rbacPermissionChecks.userActions?.(data.raw)}
        >
          {(handleClose) => {
            return (
              <PopoverOptions>
                {renderPopoverOptions(handleClose)}
              </PopoverOptions>
            );
          }}
        </PopoverController>
      )}
    </Stack>
  );
};

export const GroupedUserContextMenuRenderer: React.FC<ICellRendererParams> = ({
  data,
}) => {
  return (
    <Stack
      direction="row"
      gap="1rem"
      alignItems="center"
      justifyContent="flex-end"
      height="100%"
    >
      <UserIdentificationChips userId={data.userId} />
      {data.raw && <GroupedItemContextMenu item={data.raw} />}
    </Stack>
  );
};
